import { Head, Link } from "@inertiajs/react"
import PublicLayout from "@/components/public/layout"
import { ScrollReveal } from "@/components/ui/scroll-reveal"
import { BreadcrumbSchema } from "@/components/public/breadcrumb-schema"
import type { SeoMeta } from "@/types"
import SocialShareBar from "@/components/public/social-share-bar"

/* ------------------------------------------------------------------ */
/*  Data                                                               */
/* ------------------------------------------------------------------ */

const OPTIMISATION_AREAS = [
  {
    title: "Photography",
    description:
      "Lead with your strongest exterior shot. Use 15-30 high-quality photos in logical room-by-room order. Shoot in landscape orientation, ensure good natural light, and stage every room. Properties with professional photography get 2x more clicks on Rightmove.",
  },
  {
    title: "Property description",
    description:
      "Write for buyers, not agents. Open with the property's strongest selling point, not 'A well-presented three-bedroom semi'. Use short paragraphs, highlight lifestyle benefits, and include local knowledge. Aim for 300-500 words.",
  },
  {
    title: "Headline and key features",
    description:
      "Rightmove displays 3-5 key features prominently. Choose the features buyers search for: number of bedrooms, garden, parking, proximity to schools or stations. Avoid wasting feature slots on obvious details like 'fitted kitchen'.",
  },
  {
    title: "Floorplan",
    description:
      "Listings with floorplans get 30% more engagement on Rightmove. Include accurate room dimensions and labels. An interactive or 3D floorplan is a bonus, but even a basic measured plan adds significant value.",
  },
  {
    title: "Video and virtual tours",
    description:
      "Rightmove supports embedded video and virtual tour links. Listings with video get significantly more enquiries. A branded 30-second property video ad is more effective than a raw walkthrough and can be created from listing photos alone.",
  },
  {
    title: "Price strategy",
    description:
      "Rightmove search works in bands (e.g. 200K-250K, 250K-300K). Price at the bottom of a band rather than the top to appear in more searches. 'Guide price' and 'Offers over' are perceived differently by buyers. Choose deliberately.",
  },
]

const COMMON_MISTAKES = [
  { mistake: "Poor lead photo", fix: "Always lead with the best exterior shot in good weather and light." },
  { mistake: "Too few photos", fix: "Use 15-30 photos. More photos mean more time spent on your listing." },
  { mistake: "Portrait-orientation photos", fix: "Rightmove displays landscape. Portrait photos look terrible." },
  { mistake: "Generic description", fix: "Write unique, benefit-led copy. Avoid 'briefly comprising'." },
  { mistake: "No floorplan", fix: "30% of buyers won't enquire without one. Always include a floorplan." },
  { mistake: "Wrong price band", fix: "Price at 250K, not 260K, to appear in both 200-250K and 250-300K searches." },
  { mistake: "Cluttered rooms", fix: "Declutter and stage before photographing. Less is more." },
  { mistake: "No video content", fix: "Add a property video. Listings with video get 2x more enquiries." },
]

const CHECKLIST = [
  "Professional photography (15-30 landscape images)",
  "Strong exterior lead photo in good natural light",
  "Accurate, measured floorplan with room labels",
  "Benefit-led property description (300-500 words)",
  "5 key features chosen for search relevance",
  "EPC uploaded and current",
  "Price positioned at the bottom of a Rightmove search band",
  "Property video or virtual tour linked",
  "Correct property type, tenure, and council tax band",
  "Photos in logical room-by-room order",
]

const STATS = [
  { stat: "86%", label: "of home searches start on Rightmove" },
  { stat: "2x", label: "more clicks with professional photography" },
  { stat: "30%", label: "more engagement with a floorplan" },
  { stat: "2x", label: "more enquiries with video content" },
]

type FaqItem = { q: string; a: string }

const FAQ: FaqItem[] = [
  {
    q: "How do I get my listing to the top of Rightmove search results?",
    a: "Rightmove search results are ordered by listing date by default. New listings appear at the top. When you reduce the price, the listing gets a 'price reduced' badge and can reappear near the top. Featured listings (paid placement) appear above standard results. The best strategy is to launch with a strong listing (great photos, video, competitive price) and capture attention in the first 48 hours.",
  },
  {
    q: "How many photos should I upload to Rightmove?",
    a: "Aim for 15-30 high-quality photos. Too few (under 10) makes buyers suspicious. Too many (over 40) dilutes impact. Include all key rooms, the garden, exterior, and any standout features. Every photo should add information. Remove duplicates and poor-quality shots.",
  },
  {
    q: "Does Rightmove Premium Listing make a difference?",
    a: "Rightmove Premium Listing gives you a larger photo, prominent placement, and up to 10 displayed photos in search results. It does increase click-through rates, but the effect depends on your market. For competitive areas with many similar listings, Premium can be worth the extra cost. For unique or well-priced properties, a standard listing with excellent photography often performs just as well.",
  },
  {
    q: "How do I add video to a Rightmove listing?",
    a: "Upload your video to YouTube or Vimeo, then paste the URL into the video field in your Rightmove listing. Rightmove also supports virtual tour links (Matterport, EyeSpy360, etc.). QuiteRight creates branded property videos that are perfect for embedding on Rightmove listings.",
  },
  {
    q: "What makes a good Rightmove property description?",
    a: "Lead with the property's strongest selling point, not 'We are delighted to present'. Write short paragraphs (2-3 sentences each). Highlight lifestyle benefits, not just features: 'south-facing garden, perfect for summer entertaining' rather than just 'south-facing garden'. Include local knowledge about schools, transport, and amenities. Aim for 300-500 words.",
  },
  {
    q: "How often should I update my Rightmove listing?",
    a: "Review your listing every two weeks. If enquiry levels drop, consider refreshing the photos (new lead image, seasonal shots), updating the description, or adding video content. A price reduction triggers a notification to saved-search users. Avoid making too many small changes; each significant update should feel fresh and give buyers a reason to look again.",
  },
  {
    q: "Do Rightmove search filters affect my listing visibility?",
    a: "Yes. Buyers filter by price, bedrooms, property type, and features like parking and garden. Make sure all these fields are filled in correctly. If your property has a garage, mark it. If it has a garden, mark it. Missing data means your listing is excluded from filtered searches that it should appear in.",
  },
  {
    q: "Can I track how my Rightmove listing is performing?",
    a: "Yes. Rightmove Plus provides detailed analytics: total views, detail views, email enquiries, phone clicks, and how your listing compares to similar properties in the area. Review these metrics weekly and adjust your strategy based on what the data shows.",
  },
]

/* ------------------------------------------------------------------ */
/*  Components                                                         */
/* ------------------------------------------------------------------ */

function FaqAccordion({ items }: { items: FaqItem[] }) {
  return (
    <div className="divide-y divide-stone">
      {items.map((item) => (
        <details key={item.q} className="group py-5">
          <summary className="flex cursor-pointer items-center justify-between text-base font-medium text-charcoal">
            {item.q}
            <svg
              viewBox="0 0 20 20"
              fill="currentColor"
              className="size-5 shrink-0 text-warm-grey transition-transform group-open:rotate-180"
            >
              <path
                fillRule="evenodd"
                d="M5.22 8.22a.75.75 0 0 1 1.06 0L10 11.94l3.72-3.72a.75.75 0 1 1 1.06 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0L5.22 9.28a.75.75 0 0 1 0-1.06Z"
                clipRule="evenodd"
              />
            </svg>
          </summary>
          <p className="mt-3 text-sm leading-relaxed text-slate">{item.a}</p>
        </details>
      ))}
    </div>
  )
}

/* ------------------------------------------------------------------ */
/*  Page                                                               */
/* ------------------------------------------------------------------ */

export default function RightmoveListingOptimisationGuide({ meta }: { meta: SeoMeta }) {
  return (
    <PublicLayout>
      <Head>
        <title>{meta.title}</title>
        <meta name="description" content={meta.description} />
        <link rel="canonical" href={meta.canonical} />
        <meta property="og:title" content={meta.title} />
        <meta property="og:description" content={meta.description} />
        <meta property="og:url" content={meta.canonical} />
        <meta property="og:type" content="article" />
        <meta property="og:image" content={meta.image} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={meta.title} />
        <meta name="twitter:description" content={meta.description} />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: "How to Optimise Your Rightmove Listing for Maximum Enquiries",
            description: meta.description,
            publisher: {
              "@type": "Organization",
              name: "QuiteRight",
              url: "https://www.quiteright.co.uk",
              logo: {
                "@type": "ImageObject",
                url: "https://www.quiteright.co.uk/qr-logo-wordmark-red.png",
              },
            },
            datePublished: "2026-02-25",
            dateModified: "2026-02-25",
          })}
        </script>

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: FAQ.map((faq) => ({
              "@type": "Question",
              name: faq.q,
              acceptedAnswer: { "@type": "Answer", text: faq.a },
            })),
          })}
        </script>

        <BreadcrumbSchema
          canonical={meta.canonical}
          items={[
            { name: "Guides", path: "/guides" },
            { name: "Rightmove Listing Optimisation" },
          ]}
        />
      </Head>

      {/* Hero */}
      <section className="px-6 py-24 md:py-32">
        <div className="mx-auto max-w-4xl text-center">
          <p className="text-xs font-medium uppercase tracking-[0.2em] text-red">
            Guide
          </p>
          <h1 className="mt-4 font-display text-4xl font-bold tracking-tight text-charcoal sm:text-5xl md:text-6xl">
            Rightmove listing optimisation
            <span className="text-red">.</span>
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-lg text-slate">
            How to optimise your Rightmove listings for maximum views and
            enquiries. Photography, descriptions, pricing strategy, video, and a
            complete checklist.
          </p>
        </div>
      </section>

      <SocialShareBar />

      {/* Why it matters */}
      <section className="border-t border-stone px-6 py-16">
        <div className="mx-auto max-w-3xl">
          <h2 className="font-display text-2xl font-bold text-charcoal">
            Why Rightmove optimisation matters
            <span className="text-red">.</span>
          </h2>
          <div className="mt-4 space-y-3 text-sm leading-relaxed text-slate">
            <p>
              Rightmove is where 86% of UK property searches begin. Your
              listing is competing with dozens of similar properties for the
              same buyer's attention. The difference between a listing that
              generates 5 enquiries and one that generates 50 is almost never
              the property itself. It is how the listing is presented.
            </p>
            <p>
              Most agents upload photos, paste in a description, and move on.
              The agents who consistently win instructions and sell faster are
              the ones who treat every Rightmove listing as a marketing
              campaign: professional photography, compelling copy, strategic
              pricing, and video content.
            </p>
            <p>
              This guide covers the six areas that have the biggest impact on
              your Rightmove listing performance, plus a checklist you can use
              for every new listing.
            </p>
          </div>
        </div>
      </section>

      {/* Six areas */}
      <section className="border-t border-stone bg-cream px-6 py-20">
        <div className="mx-auto max-w-5xl">
          <ScrollReveal>
            <p className="text-center text-xs font-medium uppercase tracking-[0.2em] text-red">
              Key areas
            </p>
            <h2 className="mt-4 text-center font-display text-3xl font-bold text-charcoal md:text-4xl">
              Six things that drive Rightmove performance
              <span className="text-red">.</span>
            </h2>
          </ScrollReveal>

          <ScrollReveal stagger className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {OPTIMISATION_AREAS.map((area, i) => (
              <div key={i} className="rounded-xl border border-stone bg-white p-6">
                <h3 className="font-display text-base font-bold text-charcoal">
                  {area.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-slate">
                  {area.description}
                </p>
              </div>
            ))}
          </ScrollReveal>
        </div>
      </section>

      {/* Common mistakes */}
      <section className="border-t border-stone px-6 py-20">
        <div className="mx-auto max-w-3xl">
          <ScrollReveal>
            <p className="text-center text-xs font-medium uppercase tracking-[0.2em] text-red">
              Avoid these
            </p>
            <h2 className="mt-4 text-center font-display text-3xl font-bold text-charcoal md:text-4xl">
              Common Rightmove listing mistakes
              <span className="text-red">.</span>
            </h2>
          </ScrollReveal>

          <div className="mt-12 overflow-x-auto rounded-xl border border-stone">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-stone bg-cream/50">
                  <th className="px-4 py-3 text-left font-medium text-charcoal">Mistake</th>
                  <th className="px-4 py-3 text-left font-medium text-charcoal">Fix</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone">
                {COMMON_MISTAKES.map((row) => (
                  <tr key={row.mistake} className="hover:bg-cream/30">
                    <td className="px-4 py-3 font-medium text-charcoal">{row.mistake}</td>
                    <td className="px-4 py-3 text-slate">{row.fix}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Checklist */}
      <section className="border-t border-stone bg-cream px-6 py-20">
        <div className="mx-auto max-w-3xl">
          <ScrollReveal>
            <p className="text-center text-xs font-medium uppercase tracking-[0.2em] text-red">
              Checklist
            </p>
            <h2 className="mt-4 text-center font-display text-3xl font-bold text-charcoal md:text-4xl">
              Rightmove listing checklist
              <span className="text-red">.</span>
            </h2>
            <p className="mx-auto mt-4 max-w-xl text-center text-sm text-slate">
              Run through this checklist before publishing every listing.
            </p>
          </ScrollReveal>

          <div className="mt-10 space-y-3">
            {CHECKLIST.map((item, i) => (
              <div
                key={i}
                className="flex items-start gap-3 rounded-lg border border-stone bg-white px-4 py-3"
              >
                <svg
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  className="mt-0.5 size-4 shrink-0 text-red"
                >
                  <path
                    fillRule="evenodd"
                    d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z"
                    clipRule="evenodd"
                  />
                </svg>
                <span className="text-sm text-charcoal">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="border-t border-stone bg-charcoal px-6 py-16">
        <ScrollReveal
          stagger
          className="mx-auto grid max-w-5xl grid-cols-2 gap-px overflow-hidden rounded-xl border border-slate/20 bg-slate/20 sm:grid-cols-4"
        >
          {STATS.map((item) => (
            <div key={item.stat} className="bg-charcoal px-5 py-6 text-center">
              <p className="font-display text-2xl font-bold text-cream md:text-3xl">
                {item.stat}
              </p>
              <p className="mt-2 text-xs leading-snug text-warm-grey">
                {item.label}
              </p>
            </div>
          ))}
        </ScrollReveal>
      </section>

      {/* Related */}
      <section className="border-t border-stone px-6 py-16">
        <div className="mx-auto max-w-3xl">
          <h2 className="font-display text-2xl font-bold text-charcoal">
            Related resources<span className="text-red">.</span>
          </h2>
          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            {[
              { href: "/integrations/rightmove", title: "Rightmove integration", desc: "Create branded video ads from any Rightmove listing URL." },
              { href: "/guides/property-photography-tips", title: "Property photography tips", desc: "How to photograph properties that sell." },
            ].map((item) => (
              <Link
                key={item.href}
                href={item.href}
                className="group flex flex-col rounded-xl border border-stone bg-white p-5 transition-all hover:border-red/30 hover:shadow-sm"
              >
                <p className="font-display text-base font-bold text-charcoal transition-colors group-hover:text-red">
                  {item.title}
                </p>
                <p className="mt-1 flex-1 text-sm text-slate">{item.desc}</p>
                <p className="mt-3 flex items-center gap-1 text-xs font-medium text-red">
                  Explore
                  <svg viewBox="0 0 20 20" fill="currentColor" className="size-3.5 transition-transform group-hover:translate-x-0.5">
                    <path fillRule="evenodd" d="M5 10a.75.75 0 0 1 .75-.75h6.638L10.23 7.29a.75.75 0 1 1 1.04-1.08l3.5 3.25a.75.75 0 0 1 0 1.08l-3.5 3.25a.75.75 0 1 1-1.04-1.08l2.158-1.96H5.75A.75.75 0 0 1 5 10Z" clipRule="evenodd" />
                  </svg>
                </p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-t border-stone px-6 py-20">
        <div className="mx-auto max-w-3xl">
          <ScrollReveal className="text-center">
            <h2 className="font-display text-3xl font-bold text-charcoal md:text-4xl">
              Common questions<span className="text-red">.</span>
            </h2>
          </ScrollReveal>

          <div className="mt-12">
            <ScrollReveal>
              <FaqAccordion items={FAQ} />
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-stone bg-charcoal px-6 py-24">
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="font-display text-3xl font-bold text-cream md:text-4xl lg:text-5xl">
            Add video to every Rightmove listing
            <span className="text-red">.</span>
          </h2>
          <p className="mx-auto mt-6 max-w-xl text-lg text-warm-grey">
            QuiteRight creates branded property videos from your Rightmove
            listing URL. Paste the link, pick a template, and embed the video
            on your listing in 60 seconds.
          </p>
          <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <Link
              href="/sign-up"
              className="inline-flex w-full items-center justify-center rounded-lg bg-red px-7 py-3.5 text-base font-semibold text-white transition-colors hover:bg-deep-red sm:w-auto"
            >
              Start your free trial
            </Link>
            <Link
              href="/demo"
              className="inline-flex w-full items-center justify-center rounded-lg border border-cream/20 px-7 py-3.5 text-base font-medium text-cream transition-colors hover:border-cream/40 hover:bg-cream/5 sm:w-auto"
            >
              Book a demo
            </Link>
          </div>
        </div>
      </section>
    </PublicLayout>
  )
}
